import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, X, FileCheck, FileText, Check, CheckCircle2, XCircle,
  Percent, ShieldAlert, Lock, ArrowUpCircle, MessageCircle, Loader2,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { TokenStorage } from '../../services/api';
import ContactSalesModal from '../../components/ContactSalesModal';
import '../../styles/CheckPage.scss';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://mannaplag.mannaanalytics.com/api';

async function apiFetch(endpoint: string, options?: RequestInit) {
  const token = TokenStorage.getAccess();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options?.headers },
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

interface Checks {
  id: string;
  title: string;
  file_name: string;
  score: number | null;
  level: 'Low' | 'Moderate' | 'High' | null;
  words: number;
  date: string;
  status: 'pending' | 'processing' | 'complete' | 'failed';
  report_id?: string | null;
  error?: string | null;
}

interface CheckPageProps {
  title: string;
  subtitle: string;
  icon?: React.ComponentType<{ size?: number | string; color?: string; className?: string }>;
  accentColor: string;
  scoreLabel: string;
  checkTypes: { key: string; label: string }[];
}

type ModalStep = 'upload' | 'processing' | 'complete' | 'failed' | 'limit';

interface QuotaInfo {
  message: string;
  quota_used?: number;
  quota_limit?: number;
  plan?: string;
  upgrade_url?: string;
}

function getLevel(score: number): 'Low' | 'Moderate' | 'High' {
  if (score > 60) return 'High';
  if (score > 30) return 'Moderate';
  return 'Low';
}
function levelClass(level: string | null): string {
  if (level === 'High') return 'high';
  if (level === 'Moderate') return 'moderate';
  if (level === 'Low') return 'low';
  return 'empty';
}

function ScoreBadge({ score, level }: { score: number | null; level: string | null }) {
  if (score === null) return <span className="score-badge score-badge--empty">—</span>;
  return <span className={`score-badge score-badge--${levelClass(level)}`}>{score}%</span>;
}

function LevelBadge({ level }: { level: string | null }) {
  if (!level) return <span className="level-badge level-badge--empty">—</span>;
  return <span className={`level-badge level-badge--${levelClass(level)}`}>{level}</span>;
}

function StatusBadge({ status, error }: { status: string; error?: string | null }) {
  if (status === 'pending') return (
    <span className="status-badge status-badge--pending">
      <span className="status-badge__dot" />Queued
    </span>
  );
  if (status === 'processing') return (
    <span className="status-badge status-badge--processing">
      <span className="status-badge__dot" />Processing
    </span>
  );
  if (status === 'failed') return (
    <span className="status-badge status-badge--failed" title={error || 'Check failed'}>
      <span className="status-badge__dot" />Failed
    </span>
  );
  return null;
}

// Map checkType key → API endpoints
function getEndpoints(key: string): { list: string; upload: string } {
  switch (key) {
    case 'ai': return { list: '/checker/list/', upload: '/checker/analyze/' };
    case 'grammar': return { list: '/grammar/list/', upload: '/grammar/analyze/' };
    case 'citation': return { list: '/citation/list/', upload: '/citation/analyze/' };
    default: return { list: '/submissions/', upload: '/submissions/upload/' };
  }
}

export default function CheckPage({ title, subtitle, accentColor, scoreLabel, checkTypes }: CheckPageProps) {
  const navigate = useNavigate();
  const primaryKey = checkTypes[0]?.key ?? 'plagiarism';
  const endpoints = getEndpoints(primaryKey);
  const [checks, setChecks] = useState<Checks[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([checkTypes[0]?.key]);
  const [modalStep, setModalStep] = useState<ModalStep>('upload');
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [result, setResult] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [limitInfo, setLimitInfo] = useState<QuotaInfo | null>(null);
  const [showContactSales, setShowContactSales] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<any>(null);

  // Load submissions list
  const loadChecks = async () => {
    try {
      const data = await apiFetch(endpoints.list);
      const mapped = (Array.isArray(data) ? data : []).map((s: any) => ({
        id: s.id,
        title: s.title || s.file_name,
        file_name: s.file_name,
        score: s.overall_similarity ?? null,
        level: s.overall_similarity != null ? getLevel(s.overall_similarity) : null,
        words: s.word_count || 0,
        date: new Date(s.submitted_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }),
        status: s.status,
        report_id: s.report_id || null,
        error: s.error || null,
      }));
      setChecks(mapped);
    } catch { setChecks([]); } finally { setLoading(false); }
  };

  useEffect(() => { loadChecks(); }, []);

  // Poll a submission status
  const pollSubmission = (submissionId: string) => {
    const pollBase = primaryKey === 'ai' ? '/checker' : primaryKey === 'grammar' ? '/grammar' : primaryKey === 'citation' ? '/citation' : '/submissions';
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const data = await apiFetch(`${pollBase}/${submissionId}/`);

        if (data.status === 'processing') {
          setProgress(Math.min(30 + attempts * 5, 85));
          setProgressLabel('Analysing manuscript...');
        }

        if (data.status === 'complete') {
          clearInterval(pollRef.current);
          setProgress(100);
          setProgressLabel('Complete!');
          // Set result first, then change step after state settles
          setResult({
            ...data,
            overall_similarity: data.overall_similarity ?? null,
            report_id: data.report_id ?? null,
            title: data.title || data.file_name || '',
          });
          setTimeout(() => {
            setModalStep('complete');
            loadChecks();
          }, 300);
          return;
        }

        if (data.status === 'failed') {
          clearInterval(pollRef.current);
          setErrorMsg(data.error || 'Check failed. Please try again.');
          setTimeout(() => {
            setModalStep('failed');
            loadChecks();
          }, 300);
          return;
        }

        // Timeout after 10 minutes
        if (attempts > 120) {
          clearInterval(pollRef.current);
          setErrorMsg('Check is taking too long. Please try again later.');
          setModalStep('failed');
        }
      } catch { /* keep polling */ }
    }, 5000); // poll every 5 seconds
  };

  const handleFile = (f: File) => {
    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(f.type)) { alert('Only PDF and DOCX files are supported.'); return; }
    if (f.size > 50 * 1024 * 1024) { alert('File must be under 50MB.'); return; }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!file || selectedTypes.length === 0) return;

    setModalStep('processing');
    setProgress(10);
    setProgressLabel('Uploading manuscript...');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.[^.]+$/, ''));
    formData.append('check_type', primaryKey); // 'plagiarism' or 'ai'

    try {
      const token = TokenStorage.getAccess();
      const res = await fetch(`${BASE_URL}${endpoints.upload}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      // Always try to parse the JSON body — the API returns useful
      // error/quota details on non-2xx responses too.
      let data: any = null;
      try { data = await res.json(); } catch { /* empty / non-JSON body */ }

      if (!res.ok) {
        if (res.status === 429) {
          // Monthly plagiarism-check quota reached
          setLimitInfo({
            message: data?.error || data?.message || 'Monthly plagiarism check limit reached.',
            quota_used: data?.quota_used,
            quota_limit: data?.quota_limit,
            plan: data?.plan,
            upgrade_url: data?.upgrade_url,
          });
          setModalStep('limit');
          return;
        }

        setErrorMsg(data?.error || data?.message || `Upload failed (${res.status}). Please try again.`);
        setModalStep('failed');
        return;
      }

      setProgress(20);
      setProgressLabel('Queued for processing...');

      const submissionId = data?.submission_id || data?.id;
      if (!submissionId) {
        setErrorMsg('No submission ID returned from server.');
        setModalStep('failed');
        return;
      }

      // Start polling
      pollSubmission(submissionId);

    } catch (e: any) {
      setErrorMsg(e?.message || 'Upload failed. Please check your connection and try again.');
      setModalStep('failed');
    }
  };

  const handleClose = () => {
    clearInterval(pollRef.current);
    setShowModal(false);
    setFile(null);
    setModalStep('upload');
    setProgress(0);
    setProgressLabel('');
    setResult(null);
    setErrorMsg('');
    setLimitInfo(null);
    setShowContactSales(false);
    setSelectedTypes([checkTypes[0]?.key]);
    setPage(1);
    loadChecks();
  };

  const toggleType = (key: string) => {
    setSelectedTypes(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const totalChecks = checks.length;
  const highRisk = checks.filter(c => c.level === 'High').length;
  const clear = checks.filter(c => c.level === 'Low').length;
  const scores = checks.filter(c => c.score !== null).map(c => c.score as number);
  const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

  // Client-side pagination over the loaded checks list.
  const totalPages = Math.max(1, Math.ceil(checks.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedChecks = checks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const scoreColor = (s: number) => {
    const level = getLevel(s);
    return level === 'High' ? '#dc2626' : level === 'Moderate' ? '#d97706' : '#16a34a';
  };
  const statCards = [
    { label: 'Total docs', value: totalChecks, icon: FileText, caption: 'All time' },
    { label: 'High risk', value: highRisk, icon: ShieldAlert, caption: 'This month' },
    { label: 'Clear', value: clear, icon: CheckCircle2, caption: 'This month' },
    { label: `Avg ${scoreLabel}`, value: avgScore !== null ? `${avgScore}%` : '—', icon: Percent, caption: 'Across all checks' },
  ];

  return (
    <div className="check-page">

      {/* Topbar */}
      <div className="topbar">
        <div className="topbar__info">
          <div>
            <p className="topbar__title">{title}</p>
            <p className="topbar__subtitle">{subtitle}</p>
          </div>
        </div>
        <button onClick={() => setShowModal(true)} className="topbar__button" style={{ background: accentColor }}>
          <Upload size={13} />
          New check
        </button>
      </div>

      <div className="content">

        {/* Stats */}
        <div className="stats-grid">
          {statCards.map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-card__top">
                <p className="stat-card__label">{s.label}</p>
                <div className="stat-card__icon" style={{ background: accentColor + '18' }}>
                  <s.icon size={14} color={accentColor} />
                </div>
              </div>
              <p className="stat-card__value">{s.value}</p>
              <div className="stat-card__divider" />
              <p className="stat-card__caption">{s.caption}</p>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="table-card">
          <div className="table-head-row">
            {['#', 'Paper Title', 'File', scoreLabel, 'Level', 'Words', 'Date', 'Actions'].map(h => (
              <span key={h}>{h}</span>
            ))}
          </div>

          {loading ? (
            <div className="table-state">
              <div className="spinner" style={{ borderColor: accentColor, borderTopColor: 'transparent' }} />
              <p>Loading...</p>
            </div>
          ) : checks.length === 0 ? (
            <div className="table-state">
              {/* <Icon size={36} className="table-state__icon" /> */}
              <p className="table-state__title">No checks yet</p>
              {/* <p className="table-state__hint">Upload your first manuscript to get started</p>
              <button onClick={() => setShowModal(true)} className="table-state-btn" style={{ background: accentColor }}>
                Run first check
              </button> */}
            </div>
          ) : (
            pagedChecks.map((c, i) => (
              <div key={c.id} className="table-row">
                <p className="table-row__index">{(currentPage - 1) * PAGE_SIZE + i + 1}</p>
                <div className="table-row__title-cell">
                  <p className="table-row__title">{c.title}</p>
                  {c.status !== 'complete' && <StatusBadge status={c.status} error={c.error} />}
                </div>
                <p className="table-row__file">{c.file_name}</p>
                <ScoreBadge score={c.score} level={c.level} />
                <LevelBadge level={c.level} />
                <p className="table-row__words">{c.words > 0 ? c.words.toLocaleString() : '—'}</p>
                <p className="table-row__date">{c.date}</p>
                <div>
                  {c.status === 'complete' && c.report_id && (
                    <button
                      onClick={() => navigate(`/reports/${c.report_id}${primaryKey === 'ai' ? '?type=ai' : '?type=plagiarism'}`)}
                      className="view-btn">
                      <FileText size={12} />
                      View
                    </button>
                  )}
                </div>
              </div>
            ))
          )}

          {/* Pagination */}
          {!loading && checks.length > 0 && totalPages > 1 && (
            <div className="table-pagination">
              <p className="table-pagination__info">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, checks.length)} of {checks.length}
              </p>
              <div className="table-pagination__nav">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  className="table-pagination__btn"
                >
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === 'ellipsis' ? (
                      <span key={`e${idx}`} className="table-pagination__ellipsis">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        aria-current={p === currentPage ? 'page' : undefined}
                        className={`table-pagination__btn${p === currentPage ? ' table-pagination__btn--active' : ''}`}
                        style={p === currentPage ? { background: accentColor, borderColor: accentColor } : undefined}
                      >
                        {p}
                      </button>
                    )
                  )}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  className="table-pagination__btn"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modal ─────────────────────────────────────────── */}
      {/* Hidden (not unmounted — state is preserved) while Contact Sales is
          open, so the two overlays never render on top of each other. */}
      {showModal && !showContactSales && (
        <div className="modal-overlay">
          <div className="modal">

            {/* Modal header */}
            <div className="modal__header">
              <div className="modal__heading">
                {/* <div className="modal__icon" style={{ background: accentColor + '18' }}>
                  <Icon size={16} color={accentColor} />
                </div> */}
                <p className="modal__title">New {title}</p>
              </div>
              {modalStep !== 'processing' && (
                <button onClick={handleClose} className="modal__close">
                  <X size={18} />
                </button>
              )}
            </div>

            <div className="modal__body">

              {/* ── Step 1: Upload ── */}
              {modalStep === 'upload' && (
                <>
                  {/* Check type selector */}
                  {checkTypes.length > 1 && (
                    <div className="check-type">
                      <p className="check-type__label">Check type</p>
                      <div className="check-type__options">
                        {checkTypes.map(ct => (
                          <label key={ct.key} className={`check-type__option${selectedTypes.includes(ct.key) ? ' check-type__option--checked' : ''}`}>
                            <input type="checkbox" checked={selectedTypes.includes(ct.key)}
                              onChange={() => toggleType(ct.key)} />
                            {ct.label}
                          </label>
                        ))}
                      </div>
                      {selectedTypes.length > 1 && (
                        <p className="check-type__hint">Both checks will run sequentially.</p>
                      )}
                    </div>
                  )}

                  {/* Drop zone */}
                  <div
                    onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onClick={() => inputRef.current?.click()}
                    className={`dropzone${dragOver ? ' dropzone--dragover' : file ? ' dropzone--has-file' : ''}`}
                  >
                    <input ref={inputRef} type="file" accept=".pdf,.docx"
                      onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
                    {file ? (
                      <>
                        <FileCheck size={36} className="dropzone__icon-file" />
                        <p className="dropzone__title">{file.name}</p>
                        <p className="dropzone__subtitle">{(file.size / 1024 / 1024).toFixed(2)} MB · Click to change</p>
                      </>
                    ) : (
                      <>
                        <Upload size={36} className="dropzone__icon-empty" />
                        <p className="dropzone__hint">Drop your manuscript here</p>
                        <p className="dropzone__subtitle">PDF or DOCX · up to 50MB</p>
                      </>
                    )}
                  </div>

                  <div className="modal-actions">
                    <button onClick={handleSubmit} disabled={!file || selectedTypes.length === 0}
                      className="btn-primary" style={{ background: file ? accentColor : '#fff' }}>
                      Run check
                    </button>
                    <button onClick={handleClose} className="btn-secondary">
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {/* ── Step 2: Processing ── */}
              {modalStep === 'processing' && (
                <div className="processing">
                  <div className="processing__icon" style={{ background: accentColor + '15' }}>
                    <Loader2 size={28} color={accentColor} className="processing__spinner" />
                  </div>
                  <p className="processing__title">
                    {progress < 20 ? 'Uploading...' : progress < 90 ? `Running ${title.toLowerCase()}...` : 'Finishing up...'}
                  </p>
                  <p className="processing__subtitle">{progressLabel || 'Please wait — this takes 1–3 minutes'}</p>

                  {/* Progress bar */}
                  <div className="progress-bar">
                    <div className="progress-bar__fill" style={{ width: `${progress}%`, background: accentColor }} />
                  </div>
                  <p className="progress-percent">{progress}% complete</p>

                  {/* Steps */}
                  <div className="step-row">
                    {[
                      { label: 'Upload', done: progress >= 20 },
                      { label: 'Extract', done: progress >= 40 },
                      { label: 'Analyse', done: progress >= 80 },
                      { label: 'Report', done: progress >= 100 },
                    ].map(s => (
                      <div key={s.label} className="step">
                        <div className={`step__circle${s.done ? ' step__circle--done' : ''}`} style={s.done ? { background: accentColor } : {}}>
                          {s.done ? <Check size={11} /> : <div className="step__dot" />}
                        </div>
                        <span className={s.done ? 'step__label--done' : 'step__label'}>{s.label}</span>
                      </div>
                    ))}
                  </div>

                  <p className="processing__note">Do not close this window</p>
                </div>
              )}

              {/* ── Step 3: Complete ── */}
              {modalStep === 'complete' && result && (
                <div className="result">
                  <div className="result__icon">
                    <CheckCircle2 size={32} color="#22c55e" />
                  </div>
                  <p className="result__title">Check complete!</p>
                  <p className="result__subtitle">{result.title || result.file_name}</p>

                  {/* Score display */}
                  {result.overall_similarity != null && (
                    <div className="result-score" style={{
                      borderColor: scoreColor(result.overall_similarity) + '40',
                      background: scoreColor(result.overall_similarity) + '08',
                    }}>
                      <p className="result-score__label" style={{ color: scoreColor(result.overall_similarity) }}>
                        {scoreLabel}
                      </p>
                      <p className="result-score__value" style={{ color: scoreColor(result.overall_similarity) }}>
                        {result.overall_similarity}%
                      </p>
                      <p className="result-score__note" style={{ color: scoreColor(result.overall_similarity) }}>
                        {getLevel(result.overall_similarity)} similarity
                      </p>
                    </div>
                  )}

                  <div className="result-actions">
                    {result.report_id && (
                      <button
                        onClick={() => { handleClose(); navigate(`/reports/${result.report_id}${primaryKey === 'ai' ? '?type=ai' : '?type=plagiarism'}`); }}
                        className="result-btn-primary" style={{ background: accentColor }}>
                        <FileText size={14} />
                        View full report
                      </button>
                    )}
                    <button onClick={handleClose} className="result-btn-secondary">
                      Close
                    </button>
                  </div>
                </div>
              )}

              {/* ── Step 4: Failed ── */}
              {modalStep === 'failed' && (
                <div className="failed">
                  <div className="failed__icon">
                    <XCircle size={32} color="#ef4444" />
                  </div>
                  <p className="failed__title">Check failed</p>
                  <p className="failed__subtitle">{errorMsg || 'Something went wrong. Please try again.'}</p>
                  <div className="failed-actions">
                    <button onClick={() => { setModalStep('upload'); setErrorMsg(''); setProgress(0); }}
                      className="result-btn-primary" style={{ background: accentColor }}>
                      Try again
                    </button>
                    <button onClick={handleClose} className="result-btn-secondary">
                      Close
                    </button>
                  </div>
                </div>
              )}

              {/* ── Step 5: Monthly quota limit reached ── */}
              {modalStep === 'limit' && (
                <div className="failed">
                  <div className="failed__icon" style={{ background: '#fff7ed' }}>
                    <Lock size={30} color="#d97706" />
                  </div>
                  <p className="failed__title">Monthly limit reached</p>
                  <p className="failed__subtitle">
                    {limitInfo?.message || 'You have used all of your plagiarism checks for this month.'}
                  </p>

                  {(limitInfo?.quota_used !== undefined || limitInfo?.plan) && (
                    <div style={{
                      display: 'inline-flex', gap: '1.5rem', alignItems: 'center',
                      background: '#fafafa', border: '1px solid #eee', borderRadius: '0.625rem',
                      padding: '0.75rem 1.25rem', marginBottom: '1.5rem',
                    }}>
                      {limitInfo?.quota_used !== undefined && limitInfo?.quota_limit !== undefined && (
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: 18, fontWeight: 900, color: '#111827', margin: 0 }}>
                            {limitInfo.quota_used}/{limitInfo.quota_limit}
                          </p>
                          <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, margin: 0 }}>Checks used</p>
                        </div>
                      )}
                      {limitInfo?.plan && (
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: 18, fontWeight: 900, color: '#111827', margin: 0 }}>{limitInfo.plan}</p>
                          <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, margin: 0 }}>Current plan</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="failed-actions">
                    <button
                      onClick={() => { handleClose(); navigate(limitInfo?.upgrade_url || '/choose-plan', { state: { upgradeOnly: true } }); }}
                      className="result-btn-primary" style={{ background: accentColor }}>
                      <ArrowUpCircle size={14} />
                      Upgrade plan
                    </button>
                    <button onClick={() => setShowContactSales(true)} className="result-btn-secondary">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                        <MessageCircle size={14} />
                        Contact sales
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Contact sales popup ── */}
      <ContactSalesModal
        isOpen={showContactSales}
        onClose={() => setShowContactSales(false)}
        title="Get in touch"
        subtitle="Send us a message and our sales team will help you upgrade."
        defaultMessage={
          limitInfo
            ? `I've reached my monthly ${title.toLowerCase()} limit (${limitInfo.quota_used ?? '?'}/${limitInfo.quota_limit ?? '?'} on the ${limitInfo.plan ?? 'Free'} plan) and would like to discuss upgrading my plan.`
            : ''
        }
      />
    </div>
  );
}